import React, { useState } from 'react';
import { TaskCard } from '../TaskCard';
import { TaskItem } from '../../../../types/topic';
import { logger } from '../../../../utils/logger';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface DraggableTaskCardProps {
  task: TaskItem;
  projectId: string;
  index: number;
  itemToDelete: string | null;
  isFirst: boolean;
  isLast: boolean;
  onDelete: (id: string) => void;
  onDeleteClick: (id: string) => void;
  onCancelDelete: () => void;
  onUpdateTask: (id: string, updates: Partial<TaskItem>) => void;
  onMoveTask?: (id: string, direction: 'up' | 'down') => void;
  moveTask: (id: string, toIndex: number) => void;
  hasCollaborators?: boolean;
}

export const DraggableTaskCard: React.FC<DraggableTaskCardProps> = ({
  task,
  projectId,
  index,
  isFirst,
  isLast,
  moveTask,
  hasCollaborators,
  ...props
}) => {
  // Editing state affects if dragging is allowed
  const [isEditing, setIsEditing] = useState(false);

  // Set up DND Kit sortable functionality
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id,
    data: {
      task,
      index,
      type: 'task',
    },
    disabled: isEditing,
  });

  // Handle arrow-based reordering
  const handleMoveTask = (taskId: string, direction: 'up' | 'down') => {
    if (taskId !== task.id) {
      logger.error('Task ID mismatch in handleMoveTask', { expected: task.id, received: taskId });
      return;
    }

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    logger.component('Moving task with arrows', {
      taskId,
      direction,
      fromIndex: index,
      toIndex: newIndex,
    });

    if ((direction === 'up' && !isFirst) || (direction === 'down' && !isLast)) {
      moveTask(taskId, newIndex);
    } else {
      logger.component('Cannot move task', {
        direction,
        isFirst,
        isLast,
        message: `Cannot move ${direction} - task is at the ${direction === 'up' ? 'top' : 'bottom'}`,
      });
    }
  };

  // Styles for the draggable item with enhanced visual feedback
  const style = isDragging
    ? {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: 0.8,
        zIndex: 10,
        boxShadow: '0 8px 20px -5px rgba(0, 0, 0, 0.1), 0 4px 8px -5px rgba(0, 0, 0, 0.06)',
        cursor: 'grabbing',
      }
    : {
        // When not dragging, use minimal transform to avoid weird scaling issues
        transform: CSS.Transform.toString(transform),
        transition,
        cursor: 'grab',
      };

  return (
    <div ref={setNodeRef} style={style} {...attributes} className="relative z-[5]">
      <TaskCard
        task={{
          ...task,
          _noDragHandling: true, // Add a marker to prevent original drag behavior
        }}
        projectId={projectId}
        index={index}
        isFirst={isFirst}
        isLast={isLast}
        onMoveTask={handleMoveTask}
        hasCollaborators={hasCollaborators}
        {...props}
        customDragHandlers={{
          ...listeners,
        }}
        onEditingStateChange={setIsEditing}
      />
    </div>
  );
};
